import get from '../helpers/get';
import createPerSliceIdSelector from '../helpers/createPerSliceIdSelector';

export const getSliceCategory = (state, sliceId) => get(state, ['categorised', sliceId, 'category']);

export const getSliceSubCategory = (state, sliceId) => get(state, ['categorised', sliceId, 'subcategory']);

const sanitizeCategoryTitles = titles => titles.map(
  title => (title === 'undefined' ? undefined : title),
);

const getCategoriesMap = (state, sliceId) => get(state, ['categorised', sliceId, 'categories']) || {};

export const getSliceCategories = createPerSliceIdSelector(
  getCategoriesMap,
  categoriesMap => sanitizeCategoryTitles(Object.keys(categoriesMap)),
);

export const getSliceSubCategories = createPerSliceIdSelector(
  getCategoriesMap,
  getSliceCategory,
  (categoriesMap, category) => sanitizeCategoryTitles(Object.keys(categoriesMap[category] || {})),
);

const createHasCategoriesSelector = categoriesSelector => createPerSliceIdSelector(
  categoriesSelector,
  categories => !!categories.find(name => !!name),
);

export const sliceHasCategories = createHasCategoriesSelector(getSliceCategories);

export const sliceHasSubCategories = createHasCategoriesSelector(getSliceSubCategories);

const extractItemsWithVisibility = (categoriesMap, selectedCategory, receiveItemIds) => (
  Object.keys(categoriesMap).reduce((selectedItems, category) => [
    ...selectedItems,
    ...receiveItemIds(
      categoriesMap[category],
      category === selectedCategory || !selectedCategory || category === 'undefined',
    ),
  ], [])
);

const dedupeFavouringVisibleItems = items => Object.values(items.reduce(
  (keyedByItemId, item) => {
    const { [item.id]: existingItem, ...otherExistingItems } = keyedByItemId;
    if (existingItem && existingItem.visible) {
      // we already included this item, and it was already visible, so ignore it
      return keyedByItemId;
    }
    // either the item was not yet encountered, or it was not visible and has been removed from
    // otherExistingItems so we can safely add this item
    return {
      ...otherExistingItems,
      [item.id]: item,
    };
  },
  {},
));

export const getSliceItemIdsWithVisibility = createPerSliceIdSelector(
  getCategoriesMap,
  getSliceCategory,
  getSliceSubCategory,
  (categoriesMap, category, subCategory) => dedupeFavouringVisibleItems(
    extractItemsWithVisibility(
      categoriesMap,
      category,
      (subCategoriesMap, categoryVisible) => (
        extractItemsWithVisibility(
          subCategoriesMap,
          subCategory,
          (subCategoryItemIds, subCategoryVisible) => (
            subCategoryItemIds.map(id => ({
              id,
              visible: categoryVisible && subCategoryVisible,
            }))
          ),
        )
      ),
    ),
  ),
);
